import React from 'react';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/system';
import HorizontalStatusGroup from './HorizontalStatusGroup';
import { DASHBOARD_BAND_COLOURS } from '@/dashboard/utils/DashboardCalculations/dashboardCalculations.utils';
import { theme } from '@/core/theme/theme';

function GraphLegend({
  sx,
  location = 'dashboard',
}: {
  sx?: SxProps;
  location?: 'dashboard' | 'else';
}) {
  type ExcludeRepeatedErrors = Omit<
    typeof DASHBOARD_BAND_COLOURS,
    'POST_SUBMIT_ERRORS' | 'CRITICAL_ERRORS'
  >;
  const coloursForLegend: Record<
    keyof ExcludeRepeatedErrors,
    { colour: string; text: string; colourName: string }
  > = {
    PRE_SUBMIT_ERRORS: {
      colour: theme.palette.error.main,
      text: 'Errors',
      colourName: 'red',
    },
    BYPASSED_ERRORS: {
      colour: theme.palette.warning.light,
      text: 'Bypassed Errors',
      colourName: 'orange',
    },
    WARNINGS: {
      colour: theme.palette.custom.warningYellow as string,
      text: 'Warnings',
      colourName: 'yellow',
    },
    UNSUBMITTED: {
      colour: theme.palette.secondary.main,
      text: 'Unsubmitted',
      colourName: 'blue',
    },
    ACCEPTED: {
      colour: theme.palette.success.main,
      text: 'Accepted',
      colourName: 'green',
    },
    VALIDATED: {
      colour: theme.palette.info.light,
      text: 'Validated',
      colourName: 'sky blue',
    },
  };

  const coloursInOrder = [
    'UNSUBMITTED',
    'VALIDATED',
    'WARNINGS',
    'PRE_SUBMIT_ERRORS',
    'BYPASSED_ERRORS',
    'ACCEPTED',
  ];

  const COLOUR_AND_STATUS = coloursInOrder
    .filter((colour) => {
      if (location !== 'dashboard') {
        return ['UNSUBMITTED', 'PRE_SUBMIT_ERRORS', 'WARNINGS', 'ACCEPTED'].includes(colour);
      }
      return true;
    })
    .map((item) => {
      const colour = coloursForLegend[item as keyof ExcludeRepeatedErrors].colour;
      const label = coloursForLegend[item as keyof ExcludeRepeatedErrors].text;
      const colourName = coloursForLegend[item as keyof ExcludeRepeatedErrors].colourName;

      return {
        colour,
        label,
        colourName,
      };
    });

  return (
    <Box
      component="ul"
      display="flex"
      flexShrink="0"
      alignItems="center"
      gap={6}
      width="100%"
      sx={{
        marginTop: 'auto',
        overflowX: 'auto',
        overflowY: 'hidden',
        alignItems: 'center',
        ...sx,
      }}
      aria-label="contribution status and color information"
    >
      {COLOUR_AND_STATUS.map((status) => (
        <HorizontalStatusGroup key={status.label} {...status} />
      ))}
    </Box>
  );
}

export default GraphLegend;
